import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/Components/ui/card';
import { Button } from '@/Components/ui/button';
import { Input } from '@/Components/ui/input';
import { Alert, AlertDescription } from '@/Components/ui/alert';
import { Eye, EyeOff, LogIn, Mail, Lock, AlertCircle, ArrowLeft, Landmark, CheckCircle, X, RefreshCw, ShieldAlert, Wifi, AlertTriangle } from 'lucide-react';
import { AuthService } from '../Entities/AuthService';

export default function Login() {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [errorType, setErrorType] = useState(''); // 'validation', 'auth', 'network', 'locked', 'server'
  const [successMessage, setSuccessMessage] = useState('');
  const [failedAttempts, setFailedAttempts] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  const MAX_ATTEMPTS_BEFORE_LOCK = 5;

  // Carregar dados salvos ao inicializar (email lembrado e estado de segurança de login)
  useEffect(() => {
    const savedEmail = localStorage.getItem('rememberedEmail');
    const savedRemember = localStorage.getItem('rememberMe') === 'true';
    
    if (savedEmail && savedRemember) {
      setFormData(prev => ({
        ...prev,
        email: savedEmail
      }));
      setRememberMe(true);
    }

    // Carregar failedAttempts do localStorage (sincronizado com servidor)
    const securityStateRaw = localStorage.getItem('loginSecurityState');
    if (securityStateRaw) {
      try {
        const securityState = JSON.parse(securityStateRaw);
        const now = Date.now();
        
        // Resetar após 24h sem tentativas
        const lastAttemptTime = securityState.lastAttemptTime || 0;
        const hoursSinceLastAttempt = (now - lastAttemptTime) / (1000 * 60 * 60);
        
        if (hoursSinceLastAttempt > 24) {
          localStorage.removeItem('loginSecurityState');
          setFailedAttempts(0);
        } else if (securityState.failedAttempts > 0) {
          setFailedAttempts(securityState.failedAttempts);
        }
      } catch {
        localStorage.removeItem('loginSecurityState');
      }
    }

    // ✅ SOLUÇÃO UNIFICADA: Verificar motivo do logout
    const logoutReason = sessionStorage.getItem('logoutReason');
    if (logoutReason) {
      const messages = {
        'token_refresh_failed': 'Sua sessão expirou por inatividade. Faça login novamente.',
        'already_expired': 'Sua sessão expirou. Faça login para continuar.',
        'expired_during_use': 'Sua sessão expirou durante o uso.',
        'validation_error': 'Não foi possível validar sua sessão.',
        'validation_renewal_failed': 'Erro ao renovar sessão automaticamente.',
        'auto_renewal_failed': 'Falha ao renovar sessão.',
        'check_expiration_error': 'Erro ao verificar status da sessão.'
      };
      
      setError(messages[logoutReason] || 'Sua sessão expirou. Faça login novamente.');
      setErrorType('auth');
      
      // Limpar motivo após mostrar
      sessionStorage.removeItem('logoutReason');
    }

    // Verificar se há mensagem de sucesso do state (ex: após verificação de email)
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      // Limpar o state para não mostrar novamente ao recarregar
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // ✅ Limpar APENAS erros de validação ao digitar (UX responsiva)
    // ✅ MANTER erros de autenticação, rede e bloqueio
    if (error && errorType === 'validation') {
      setError('');
      setErrorType('');
    }
  };

  // ✅ Validação frontend robusta
  const validateForm = () => {
    // Validar email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      setError('Por favor, informe seu email.');
      setErrorType('validation');
      return false;
    }
    if (!emailRegex.test(formData.email)) {
      setError('Email inválido. Verifique o formato (exemplo@dominio.com).');
      setErrorType('validation');
      return false;
    }

    // Validar senha
    if (!formData.password) {
      setError('Por favor, informe sua senha.');
      setErrorType('validation');
      return false;
    }
    if (formData.password.length < 6) {
      setError('Senha muito curta. Deve ter no mínimo 6 caracteres.');
      setErrorType('validation');
      return false;
    }

    return true;
  };

  // ✅ Função para limpar erros manualmente
  const clearError = () => {
    setError('');
    setErrorType('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // ✅ Validar formulário antes de enviar
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    // ✅ NÃO limpar erro automaticamente - usuário deve ler a mensagem

    try {
      const response = await AuthService.login(formData.email, formData.password);
      
      // ✅ SUCESSO: Backend retornou 200 com success: true
      if (response?.success) {
        // Gerenciar "Lembrar-me"
        if (rememberMe) {
          localStorage.setItem('rememberedEmail', formData.email);
          localStorage.setItem('rememberMe', 'true');
        } else {
          localStorage.removeItem('rememberedEmail');
          localStorage.removeItem('rememberMe');
        }
        
        // Resetar estado de segurança
        setFailedAttempts(0);
        localStorage.removeItem('loginSecurityState');

        // Limpar erros e mostrar sucesso
        setError('');
        setErrorType('');
        setSuccessMessage('✅ Login realizado com sucesso! Redirecionando...');

        setTimeout(() => {
          navigate('/dashboard');
        }, 800);
      }
    } catch (err) {
      console.error('Erro ao fazer login:', err);
      
      const statusCode = err.response?.status;
      const errorData = err.response?.data || {};
      const errorCode = err.code;
      
      // ✅ 0. ERRO DE REDE (timeout, sem conexão, DNS failure)
      // Sistema já está fazendo retry automático (3 tentativas)
      // NÃO conta como tentativa de login
      if (!err.response || errorCode === 'ECONNABORTED' || errorCode === 'ERR_NETWORK' || errorCode === 'ETIMEDOUT') {
        setError('⚠️ Problema de conexão detectado. O sistema está tentando reconectar automaticamente...');
        setErrorType('network');
        setIsLoading(false);
        return;
      }
      
      // ✅ 1. PRIORIDADE: Status 423 (Conta bloqueada no SERVIDOR por SENHA ERRADA)
      if (statusCode === 423) {
        const lockedUntil = errorData.locked_until;
        const serverAttempts = errorData.login_attempts || 5;
        
        // Resetar estado local e respeitar servidor
        setFailedAttempts(serverAttempts);
        localStorage.removeItem('loginSecurityState');
        
        if (lockedUntil) {
          const lockedUntilDate = new Date(lockedUntil);
          const now = new Date();
          const diffMs = lockedUntilDate - now;
          const diffMinutes = Math.ceil(diffMs / 60000);
          
          if (diffMinutes > 0) {
            setError(`🔒 Conta bloqueada por múltiplas tentativas com senha incorreta. Tente novamente em ${diffMinutes} minuto${diffMinutes > 1 ? 's' : ''}.`);
          } else {
            setError('🔒 Sua conta foi bloqueada. O bloqueio expirou, você pode tentar novamente.');
          }
        } else {
          setError('🔒 Conta bloqueada por segurança. Aguarde alguns minutos.');
        }
        
        setErrorType('locked');
        setIsLoading(false);
        return;
      }
      
      // ✅ 2. Status 429 (Rate Limit - Muitas tentativas com SENHA ERRADA)
      if (statusCode === 429) {
        setError('🔒 Muitas tentativas com senha incorreta. Por favor, aguarde 15 minutos antes de tentar novamente.');
        setErrorType('locked');
        setIsLoading(false);
        return;
      }
      
      // ✅ 3. Status 401/403 (Credenciais incorretas, conta inativa, email não verificado)
      if (statusCode === 401 || statusCode === 403) {
        // ✅ SINCRONIZAR com servidor: usar login_attempts do backend
        const serverAttempts = errorData.login_attempts;
        
        if (serverAttempts !== undefined) {
          // Servidor enviou tentativas: usar valor do servidor
          setFailedAttempts(serverAttempts);
          
          localStorage.setItem('loginSecurityState', JSON.stringify({
            failedAttempts: serverAttempts,
            lastAttemptTime: Date.now()
          }));
        }
        
        // Usar mensagem do servidor
        const errorMessage = errorData.message || 'Email ou senha incorretos. Verifique suas credenciais.';
        setError(`❌ ${errorMessage}`);
        setErrorType('auth');
        setIsLoading(false);
        return;
      }
      
      // ✅ 4. Erro 500+ (Servidor com problema - NÃO culpar usuário)
      if (statusCode >= 500) {
        setError('⚠️ Erro no servidor. Tente novamente em alguns instantes.');
        setErrorType('server');
        setIsLoading(false);
        return;
      }
      
      // ✅ 5. Outros erros não mapeados
      setError('⚠️ Erro inesperado. Tente novamente.');
      setErrorType('network');
      setIsLoading(false);
      return;
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = () => {
    navigate('/request-password-reset');
  };

  const handleBackToHome = () => {
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-blue-50 to-slate-50 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background decorativo institucional */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#003098_1px,transparent_1px),linear-gradient(to_bottom,#003098_1px,transparent_1px)] bg-[size:4rem_4rem] opacity-[0.03]" />
      <div className="absolute top-20 left-10 w-96 h-96 bg-[#003098]/10 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-10 w-[500px] h-[500px] bg-[#0074f6]/10 rounded-full blur-3xl" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#efb110]/5 rounded-full blur-3xl" />
      
      {/* Botão voltar */}
      <Button
        onClick={handleBackToHome}
        variant="ghost"
        className="absolute top-4 left-4 sm:top-6 sm:left-6 flex items-center gap-2 text-[#003098] hover:text-[#0074f6] hover:bg-[#003098]/10 transition-colors z-50 font-medium h-auto py-2 px-3 sm:py-2.5 sm:px-4 min-h-[44px] min-w-[44px] touch-manipulation"
      >
        <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
        <span className="text-xs sm:text-sm md:text-base">Voltar</span>
      </Button>
      
      <div className="w-full max-w-md relative z-10 px-2 sm:px-0">
        {/* Logo e Título */}
        <div className="text-center mb-6 sm:mb-8 mt-12 sm:mt-0">
          <div className="inline-flex items-center justify-center mb-4 sm:mb-6">
            <img 
              src="/brazao_logo.png" 
              alt="Prefeitura de Corumbá" 
              className="h-16 sm:h-20 w-auto"
            />
          </div>
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#003098] mb-2 px-4">Painel de Controle</h1>
          <p className="text-sm sm:text-base text-[#0074f6] font-medium px-4">Monitoramento de Parcerias com OSC</p>
          <p className="text-slate-600 text-xs sm:text-sm mt-1 px-4">Prefeitura Municipal de Corumbá/MS</p>
        </div>

        <Card className="shadow-2xl bg-white/80 backdrop-blur-xl border-2 border-[#003098]/20">
          <CardHeader className="space-y-1 border-b-2 border-[#003098]/10 px-4 sm:px-6 py-4 sm:py-6">
            <CardTitle className="text-lg sm:text-xl md:text-2xl font-bold text-center text-[#003098]">Entrar no Sistema</CardTitle>
            <p className="text-slate-700 text-center text-xs sm:text-sm md:text-base">
              Digite suas credenciais para acessar a plataforma
            </p>
          </CardHeader>
          
          <CardContent className="px-4 sm:px-6 py-4 sm:py-6">
            <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4" aria-label="Formulário de login">
              {successMessage && (
                <Alert className="border-green-600 bg-green-50 animate-in fade-in-0 slide-in-from-top-2 duration-300 relative">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <AlertDescription className="text-green-800 font-medium leading-relaxed flex-1">
                      {successMessage}
                    </AlertDescription>
                    <button
                      onClick={() => setSuccessMessage('')}
                      className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
                      aria-label="Fechar alerta"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </Alert>
              )}
              
              {error && (
                <Alert className={`
                  relative animate-in fade-in-0 slide-in-from-top-2 duration-300
                  ${errorType === 'locked' ? 'border-red-600 bg-red-50' : ''}
                  ${errorType === 'auth' ? 'border-amber-500 bg-amber-50' : ''}
                  ${errorType === 'network' ? 'border-blue-500 bg-blue-50' : ''}
                  ${errorType === 'validation' ? 'border-orange-500 bg-orange-50' : ''}
                  ${!errorType ? 'border-red-500 bg-red-50' : ''}
                `}>
                  <div className="flex items-start gap-3">
                    {errorType === 'locked' && <ShieldAlert className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />}
                    {errorType === 'auth' && <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />}
                    {errorType === 'network' && <Wifi className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />}
                    {errorType === 'validation' && <AlertCircle className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />}
                    {!errorType && <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />}
                    
                    <div className="flex-1">
                      <AlertDescription className={`
                        font-medium leading-relaxed
                        ${errorType === 'locked' ? 'text-red-800' : ''}
                        ${errorType === 'auth' ? 'text-amber-800' : ''}
                        ${errorType === 'network' ? 'text-blue-800' : ''}
                        ${errorType === 'validation' ? 'text-orange-800' : ''}
                        ${!errorType ? 'text-red-800' : ''}
                      `}>
                        {error}
                      </AlertDescription>
                      
                      {/* ✅ Botão "Tentar Novamente" para erros de rede */}
                      {errorType === 'network' && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            clearError();
                            // Criar evento sintético para reenviar formulário
                            const form = e.target.closest('form');
                            if (form) {
                              const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
                              form.dispatchEvent(submitEvent);
                            }
                          }}
                          className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors shadow-md hover:shadow-lg"
                          disabled={isLoading}
                        >
                          <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                          {isLoading ? 'Tentando...' : 'Tentar Novamente'}
                        </button>
                      )}
                    </div>
                    
                    {/* ✅ Botão fechar (exceto para erros de bloqueio) */}
                    {errorType !== 'locked' && (
                      <button
                        onClick={clearError}
                        className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
                        aria-label="Fechar alerta"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </Alert>
              )}

              <div className="space-y-2">
                <label htmlFor="email" className="text-xs sm:text-sm font-semibold text-[#003098]">
                  Email Institucional
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#003098]/60 w-4 h-4" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="seu.email@corumba.ms.gov.br"
                    className={`pl-10 text-sm sm:text-base h-10 sm:h-11 transition-all ${
                      isLoading ? 'opacity-60 cursor-not-allowed bg-slate-100' : ''
                    } ${
                      // ✅ Borda vermelha para erros de validação de email
                      (errorType === 'validation' && error.toLowerCase().includes('email'))
                        ? 'border-2 border-red-500 focus:border-red-600 focus:ring-red-500'
                        : ''
                    } ${
                      // ✅ Borda âmbar para erros de autenticação
                      (errorType === 'auth')
                        ? 'border-2 border-amber-400 focus:border-amber-500 focus:ring-amber-400'
                        : ''
                    }`}
                    required
                    disabled={isLoading}
                    aria-label="Email institucional"
                    aria-required="true"
                    aria-invalid={errorType === 'validation' && error.includes('email') ? 'true' : 'false'}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label htmlFor="password" className="text-xs sm:text-sm font-semibold text-[#003098]">
                  Senha
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#003098]/60 w-4 h-4" />
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="Digite sua senha"
                    className={`pl-10 pr-10 text-sm sm:text-base h-10 sm:h-11 transition-all ${
                      isLoading ? 'opacity-60 cursor-not-allowed bg-slate-100' : ''
                    } ${
                      // ✅ Borda vermelha para erros de validação de senha
                      (errorType === 'validation' && error.toLowerCase().includes('senha'))
                        ? 'border-2 border-red-500 focus:border-red-600 focus:ring-red-500'
                        : ''
                    } ${
                      // ✅ Borda âmbar para erros de autenticação
                      (errorType === 'auth')
                        ? 'border-2 border-amber-400 focus:border-amber-500 focus:ring-amber-400'
                        : ''
                    }`}
                    required
                    disabled={isLoading}
                    aria-label="Senha"
                    aria-required="true"
                    aria-invalid={errorType === 'validation' && error.includes('senha') ? 'true' : 'false'}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#003098]/60 hover:text-[#003098] transition-colors"
                    disabled={isLoading}
                    aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-0">
                <div className="flex items-center">
                  <input
                    id="remember"
                    name="remember"
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="h-4 w-4 text-[#003098] focus:ring-[#003098] border-slate-300 rounded"
                    disabled={isLoading}
                  />
                  <label htmlFor="remember" className="ml-2 block text-xs sm:text-sm text-slate-700 cursor-pointer font-medium">
                    Lembrar-me
                  </label>
                </div>
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  className="text-xs sm:text-sm text-[#0074f6] hover:text-[#003098] font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isLoading}
                >
                  Esqueceu a senha?
                </button>
              </div>

              <div>
                <Button
                  type="submit"
                  className={`w-full bg-gradient-to-r from-[#003098] to-[#0074f6] hover:from-[#002070] hover:to-[#0060d0] text-white shadow-xl shadow-[#003098]/30 font-semibold text-sm sm:text-base py-5 sm:py-6 transition-all ${
                    isLoading ? 'opacity-60 cursor-not-allowed' : ''
                  }`}
                  disabled={isLoading}
                  aria-label="Entrar no sistema"
                  aria-busy={isLoading ? 'true' : 'false'}
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      <span className="text-sm sm:text-base">Entrando...</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center">
                      <LogIn className="w-4 h-4 mr-2" />
                      <span className="text-sm sm:text-base">Entrar</span>
                    </div>
                  )}
                </Button>

                {/* ✅ Card de avisos: Mostra tentativas restantes baseado no SERVIDOR */}
                {failedAttempts > 0 ? (
                  <div className="mt-3 p-3 sm:p-4 bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-400 rounded-xl shadow-md animate-in fade-in-0 slide-in-from-bottom-2 duration-300">
                    <div className="flex items-center justify-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-amber-600" />
                      <p className="text-sm sm:text-base text-center text-amber-900 font-semibold leading-relaxed">
                        Tentativas restantes: <span className="font-bold text-amber-700 text-lg">{MAX_ATTEMPTS_BEFORE_LOCK - failedAttempts}</span> de {MAX_ATTEMPTS_BEFORE_LOCK}
                      </p>
                    </div>
                    <div className="mt-2 w-full bg-amber-200 rounded-full h-2 overflow-hidden">
                      <div 
                        className="bg-amber-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${((MAX_ATTEMPTS_BEFORE_LOCK - failedAttempts) / MAX_ATTEMPTS_BEFORE_LOCK) * 100}%` }}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="mt-3 p-3 sm:p-4 bg-gradient-to-r from-blue-50 to-slate-50 border-2 border-blue-300 rounded-xl shadow-sm">
                    <div className="flex items-center justify-center gap-2">
                      <AlertCircle className="w-5 h-5 text-blue-600" />
                      <p className="text-sm sm:text-base text-center text-slate-700 font-medium leading-relaxed">
                        Você tem <span className="font-bold text-blue-700">{MAX_ATTEMPTS_BEFORE_LOCK} tentativas</span> antes de bloqueio
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </form>

            <div className="mt-4 sm:mt-6 text-center pt-4 sm:pt-6 border-t border-[#003098]/10">
              <p className="text-xs sm:text-sm text-slate-600 font-medium px-2">
                📧 Suporte técnico disponível em horário comercial
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="mt-6 sm:mt-8 text-center px-2">
          <div className="inline-flex items-center space-x-2 sm:space-x-3 px-4 sm:px-6 py-2.5 sm:py-3 bg-white/60 backdrop-blur-sm rounded-full border border-[#003098]/20">
            <Landmark className="w-3 h-3 sm:w-4 sm:h-4 text-[#003098] flex-shrink-0" />
            <p className="text-xs sm:text-sm text-slate-700 font-medium">
              &copy; {new Date().getFullYear()} Prefeitura Municipal de Corumbá/MS
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
